<template>
  <b-row class="tabs margin-0">
    <b-col
      v-if="isDesktopTabs"
      cols="12"
      lg="auto"
      class="padding-0 padding-left-24 mobile-none tablet-none width-324 z-index-2"
    >
      <ul class="desktop-tabs__items">
        <slot name="extra-options"></slot>
        <LoadingWrapper :loading="!generateTabs.length">
          <template #loading>
            <span class="width-percent-100 flex-start flex-direction-column">
              <b-skeleton width="100%" class="margin-bottom-16"></b-skeleton>
              <b-skeleton width="100%" class="margin-bottom-16"></b-skeleton>
              <b-skeleton width="100%" class="margin-bottom-16"></b-skeleton>
              <b-skeleton width="100%" class="margin-bottom-16"></b-skeleton>
            </span>
          </template>
          <template #default>
            <ul class="margin-0 padding-0">
              <li
                v-for="(item, index) in generateTabs"
                :key="`a-${index}`"
                :class="`desktop-tabs__item flex-between ${
                  index === currentTabIndex ? 'desktop-tabs__item--active' : ''
                } ${item.class}`"
                @click.stop="handleSelectTab(index)"
              >
                <div>
                  <slot name="right-icon-item" :item="item"></slot>
                  <span class="font-size-14 color-complementary-400">
                    {{ item.text }}
                  </span>
                </div>
                <slot name="left-icon-item" :item="item"></slot>
              </li>
            </ul>
          </template>
        </LoadingWrapper>
      </ul>
    </b-col>
    <b-col
      v-if="!isMobileTabsModal"
      cols="12"
      class="padding-0"
      :class="{ 'desktop-none': isDesktopTabs }"
    >
      <ul class="tabs__items">
        <li
          class="tabs__item"
          :class="{ 'tabs__item--active': index === currentTabIndex }"
          v-for="({ text }, index) in generateTabs"
          :key="`b-${index}`"
          @click.stop="handleSelectTab(index)"
        >
          {{ text }}
        </li>
      </ul>
    </b-col>
    <b-col v-if="isMobileTabsModal" cols="12" class="padding-0 desktop-none">
      <slot name="extra-options"></slot>
      <DropdownModal ref="dropdownModalReference">
        <template #click-area>
          <div
            v-if="computeCurrentTab"
            class="background-surface border--solid border--complementary-50 border-radius-8 padding-y-8 margin-bottom-8 flex-center is-hover cursor--pointer"
          >
            <div class="color-complementary-400">
              <slot name="right-icon-item" :item="computeCurrentTab"></slot>
              <span class="font-size-14">
                {{ computeCurrentTab.text }}
              </span>
              <span class="margin-right-8">
                <mdicon name="chevron-down" size="20" />
              </span>
            </div>
          </div>
        </template>
        <template #content>
          <LoadingWrapper :loading="!generateTabs.length">
            <template #loading>
              <span class="width-percent-100 flex-start flex-direction-column">
                <b-skeleton width="100%" class="margin-bottom-16"></b-skeleton>
                <b-skeleton width="100%" class="margin-bottom-16"></b-skeleton>
                <b-skeleton width="100%" class="margin-bottom-16"></b-skeleton>
                <b-skeleton width="100%" class="margin-bottom-16"></b-skeleton>
              </span>
            </template>
            <template #default>
              <ul class="margin-0 padding-0">
                <li
                  v-for="(tab, index) in generateTabs"
                  :key="`c-${index}`"
                  :class="{
                    'desktop-tabs__item--active': index === currentTabIndex,
                  }"
                  class="desktop-tabs__item flex-between"
                  @click.stop="handleSelectTab(index, handleCloseDropdownModal)"
                >
                  <div>
                    <slot name="right-icon-item" :item="tab"></slot>
                    <span class="font-size-14 color-complementary-400">
                      {{ tab.text }}
                    </span>
                  </div>
                  <slot name="left-icon-item" :item="tab"></slot>
                </li>
              </ul>
            </template>
          </LoadingWrapper>
        </template>
      </DropdownModal>
    </b-col>
    <b-col
      cols="12"
      :lg="isDesktopTabs ? '' : '12'"
      class="padding-0 overflow-x-hidden"
      :class="isDesktopTabs ? 'desktop-tabs__content' : 'tabs__content'"
    >
      <slot :name="`tab-${currentTabIndex}`"></slot>
    </b-col>
  </b-row>
</template>

<script>
import { LoadingWrapper } from "@/components/re-design";
import DropdownModal from "../modal/DropdownModal.vue";
import { computed, onMounted, ref, toRefs } from "vue";
import { is, pathOr } from "ramda";
export default {
  components: { LoadingWrapper, DropdownModal },
  props: {
    tabs: {
      type: Array,
      default: () => [],
    },
    isDesktopTabs: {
      type: Boolean,
      default: false,
    },
    isMobileTabsModal: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit }) {
    // generate props data
    const { tabs, isDesktopTabs, isMobileTabsModal } = toRefs(props);

    const currentTabIndex = ref(0);

    const generateTabs = computed(() => {
      return tabs.value.map((item, index) => {
        return is(String, item) ? { icon: "", text: item } : item;
      });
    });

    // select current tab with index
    const handleSelectTab = (selectedIndex = 0, callback = () => {}) => {
      currentTabIndex.value = selectedIndex;
      const currentTab = generateTabs.value[selectedIndex];
      emit("getCurrentTab", currentTab);
      callback();
    };
    const computeCurrentTab = computed(
      () => generateTabs.value[currentTabIndex.value]
    );

    // select first tab when mounted the component
    onMounted(() => handleSelectTab(0));

    // call the method "handleCloseContentMenu" of "DropdownModal" component with reference for close mobile modal when clicked a tab
    const dropdownModalReference = ref(null);
    const handleCloseDropdownModal = () =>
      dropdownModalReference.value.handleCloseContentMenu();
    // handleCloseContentMenu

    // return data to be used in template
    return {
      isDesktopTabs,
      isMobileTabsModal,
      computeCurrentTab,
      currentTabIndex,
      generateTabs,
      handleSelectTab,
      pathOr,
      dropdownModalReference,
      handleCloseDropdownModal,
    };
  },
};
</script>
